import type { CartItemInputDto, RollLayoutDto } from "@sticker-giant/sg-frontend-common";

export type RollDriver = NonNullable<CartItemInputDto["rollDriver"]> | null;

/** The DTO field is optional for older items; missing means No Preference. */
export function getRollDriver(item: { rollDriver?: RollDriver }): RollDriver {
  return item.rollDriver ?? null;
}

export interface RollSelection {
  rollCount: number | null;
  rollQty: number | null;
}

export interface RollDriverSelection extends RollSelection {
  rollDriver: RollDriver;
}

export const NO_PREFERENCE_VALUE = "NO_PREFERENCE";
export const NO_PREFERENCE_LABEL = "No preference";

export const FEWEST_ROLLS_VALUE = "fewest-rolls";
export const FEWEST_ROLLS_LABEL = "Fewest rolls possible";
export const FEWEST_ROLLS_SELECTION: RollDriverSelection = { rollDriver: "FEWEST_ROLLS", rollCount: 1, rollQty: null };

export function offersFewestRolls(driver: RollDriver) {
  return driver === "ROLL_COUNT" || driver === "FEWEST_ROLLS";
}

export interface ValidRollOption {
  itemsPerRoll: number;
  numRolls: number;
  outsideDiameter?: string;
}

/** Valid roll options; the `minNumberOfRolls` option is dropped only where "Fewest rolls possible" replaces it. */
export function buildRollOptions(rollLayout: RollLayoutDto | undefined, driver: RollDriver): ValidRollOption[] {
  const minRolls = offersFewestRolls(driver) ? (rollLayout?.minNumberOfRolls ?? 0) : 0;
  return (
    rollLayout?.rollOptions
      ?.filter(
        (option): option is ValidRollOption =>
          option?.itemsPerRoll !== undefined && option.itemsPerRoll > 0 && option.numRolls !== undefined,
      )
      .filter(option => option.numRolls > minRolls) ?? []
  );
}

export function toRollOptionValue(rollCount: number | null, rollQty: number | null) {
  return `${rollCount}:${rollQty}`;
}

/** Leaving "Fewest rolls" for a concrete option or No Preference switches the driver back to "Number of rolls". */
export function selectionFromOption(driver: RollDriver, { rollCount, rollQty }: RollSelection): RollDriverSelection {
  return { rollDriver: driver === "FEWEST_ROLLS" ? "ROLL_COUNT" : driver, rollCount, rollQty };
}

/** The roll select's value; `selection` is null when nothing in the current options is selected.
 * Without one it is blank (placeholder), or No Preference when `fallBackToNoPreference`. */
export function rollSelectValue(driver: RollDriver, selection: RollSelection | null, fallBackToNoPreference = false) {
  if (driver === "FEWEST_ROLLS") return FEWEST_ROLLS_VALUE;
  if (selection) return toRollOptionValue(selection.rollCount, selection.rollQty);
  return fallBackToNoPreference ? NO_PREFERENCE_VALUE : "";
}

/** Finds the option matching the previous selection on the dimension the driver preserves. */
export function resolveRollByDriver(
  options: ValidRollOption[],
  driver: RollDriver,
  { rollCount, rollQty }: RollSelection,
): ValidRollOption | null {
  if (driver === "ROLL_COUNT") return options.find(option => option.numRolls === rollCount) ?? null;
  if (driver === "ROLL_QTY") return options.find(option => option.itemsPerRoll === rollQty) ?? null;
  return null;
}

/** The roll selection to carry over to new options; FEWEST_ROLLS always stays at one roll. */
export function carryOverRollSelection(
  options: ValidRollOption[],
  driver: RollDriver,
  selection: RollSelection,
): RollSelection {
  if (driver === "FEWEST_ROLLS")
    return { rollCount: FEWEST_ROLLS_SELECTION.rollCount, rollQty: FEWEST_ROLLS_SELECTION.rollQty };
  const match = resolveRollByDriver(options, driver, selection);
  return { rollCount: match?.numRolls ?? null, rollQty: match?.itemsPerRoll ?? null };
}

/** Whether to show the lost-selection UI: a quantity change this session discarded the item's roll quantity
 * and it is still unset. Otherwise a preference with no roll quantity is No Preference. */
export function needsRollSelection(
  { rollDriver, rollCount }: { rollDriver: RollDriver; rollCount: number | null },
  isLost: boolean,
) {
  return isLost && rollDriver !== null && rollCount === null;
}
